import React from 'react'
import dayjs from 'dayjs'
import Loader from '../ui_custom/loader'

// types
type HotmartDataProps = {
  hotmartData: unknown
  isLoading: boolean
}

// variables
const loc = 'components/common/hotmart-data'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md transition">
      <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
        <span className="text-lg font-semibold text-gray-700">{title}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-gray-400">detalhes</span>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="min-w-[8rem] text-xs font-semibold text-gray-500">{label}:</span>
      <span className="text-sm text-gray-800 break-words">{value || 'N/A'}</span>
    </div>
  )
}

function EmptyState({ isNotFound = false }: { isNotFound: boolean }) {
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-gray-600">
      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">🔍</div>
      <p className="text-sm">{!isNotFound ? 'Busque um email para ver os dados de assinatura e compra validados.' : 'Não foi encontrado dados na Hotmart.'}</p>
    </div>
  )
}

function fmtProduct(prod: { id: string; name: string }) {
  if (!prod) return 'N/A'
  const bits = [prod.id, prod.name].filter(Boolean)
  return bits.length ? bits.join(' – ') : 'N/A'
}

function joinNonEmpty(arr: string[]) {
  return (arr || []).filter(Boolean).join(' – ')
}

export function HotmartData({ hotmartData, isLoading }: HotmartDataProps) {
  return isLoading ? (
    <Card title="Carregando...">
      <div className="w-full flex justify-center items-center">
        <Loader variant="spinner" size="sm" />
      </div>
    </Card>
  ) : (
    <div className="w-full mx-auto mt-4">
      {hotmartData?.sale || hotmartData?.subscription ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Plano">
            <InfoRow label="Produto" value={fmtProduct(hotmartData?.subscription?.product)} />
            <InfoRow label="Status" value={hotmartData?.subscription?.status || 'N/A'} />
            <InfoRow label="Assinante" value={joinNonEmpty([hotmartData?.subscription?.subscriber?.name, hotmartData?.subscription?.subscriber?.email]) || 'N/A'} />
            <InfoRow label="Data da requisição" value={hotmartData?.subscription?.request_date ? dayjs(new Date(hotmartData?.subscription?.request_date)).format('DD/MM/YYYY') : 'N/A'} />
          </Card>

          <Card title="Compra" isLoading={isLoading}>
            <InfoRow label="Produto" value={fmtProduct(hotmartData?.sale?.product)} />
            <InfoRow label="Comprador" value={joinNonEmpty([hotmartData?.sale?.buyer?.name, hotmartData?.sale?.buyer?.email]) || 'N/A'} />
            <InfoRow label="Status" value={hotmartData?.sale?.purchase?.status || 'N/A'} />
            <InfoRow label="Data da compra" value={hotmartData?.sale?.purchase?.approved_date ? dayjs(new Date(hotmartData?.sale?.purchase?.approved_date)).format('DD/MM/YYYY') : 'N/A'} />
            <InfoRow label="Pagamento" value={hotmartData?.sale?.purchase?.payment?.method || 'N/A'} />
          </Card>
        </div>
      ) : (
        <EmptyState isNotFound={hotmartData?.sale == null && hotmartData?.subscription == null} />
      )}

      <style>{`
          @keyframes loading {
          0% { transform: translateX(-100%);}
          50% { transform: translateX(30%);}
          100% { transform: translateX(120%);}
          }
        `}</style>
    </div>
  )
}
